import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  FaCheckCircle,
  FaFileAlt,
  FaCalendarAlt,
  FaUserGraduate,
} from "react-icons/fa";
import "./Admissions.css";

const Admissions = () => {
  const [formData, setFormData] = useState({
    parentName: "",
    email: "",
    phone: "",
    studentName: "",
    dateOfBirth: "",
    grade: "",
    institution: "",
    previousSchool: "",
    message: "",
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await axios.post("/api/admission-inquiry", formData);
      setStatus({ type: "success", message: response.data.message });
      setFormData({
        parentName: "",
        email: "",
        phone: "",
        studentName: "",
        dateOfBirth: "",
        grade: "",
        institution: "",
        previousSchool: "",
        message: "",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.error ||
          "Failed to submit inquiry. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const admissionSteps = [
    {
      icon: <FaFileAlt />,
      title: "Submit Inquiry",
      description:
        "Fill out the online admission inquiry form with student details",
    },
    // {
    //   icon: <FaCalendarAlt />,
    //   title: "Schedule Visit",
    //   description: "Visit the campus and meet our faculty and staff"
    // },
    {
      icon: <FaUserGraduate />,
      title: "Entrance Test",
      description: "Take entrance test for grades VI and above (if applicable)",
    },
    {
      icon: <FaCheckCircle />,
      title: "Admission Confirmation",
      description: "Complete documentation and secure your child's admission",
    },
  ];

  const grades = [
    "Nursery",
    "LKG",
    "UKG",
    "Grade 1",
    "Grade 2",
    "Grade 3",
    "Grade 4",
    "Grade 5",
    "Grade 6",
    "Grade 7",
    "Grade 8",
    "Grade 9",
    "Grade 10",
    "Grade 11",
    "Grade 12",
  ];

  const institutions = [
    "CBSE School",
    "ICSE School",
    "Cambridge School",
    "State Syllabus",
  ];

  return (
    <div className="admissions-page">
      <section className="page-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Admissions</h1>
            <p>
              Begin your child's journey to excellence with Sri Vani Education
            </p>
          </motion.div>
        </div>
      </section>

      {/* Admission Process */}
      <section className="admission-process-section section">
        <div className="container">
          <h2 className="section-title">Admission Process</h2>
          <p className="section-subtitle">
            A simple four-step process to secure your child's future
          </p>
          <div className="grid grid-4">
            {admissionSteps.map((step, index) => (
              <motion.div
                key={index}
                className="process-card card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="process-icon">{step.icon}</div>
                <div className="process-number">0{index + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Dates */}
      <section className="dates-section section">
        <div className="container">
          <h2 className="section-title">Important Dates 2024-25</h2>
          <div className="dates-grid">
            <motion.div
              className="date-card card"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3>Admissions Open</h3>
              <p className="date-text">January 15, 2024</p>
              <p>Applications for academic year 2024-25 are now open</p>
            </motion.div>

            <motion.div
              className="date-card card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>Entrance Tests</h3>
              <p className="date-text">March 1-15, 2024</p>
              <p>Scheduled entrance examinations for grades VI and above</p>
            </motion.div>

            <motion.div
              className="date-card card"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3>Academic Year Begins</h3>
              <p className="date-text">June 1, 2024</p>
              <p>First day of classes for all students</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="inquiry-form-section section">
        <div className="container">
          <motion.div
            className="form-wrapper"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Admission Inquiry Form</h2>
            <p className="section-subtitle">
              Fill out the form below and our admissions team will contact you
              soon
            </p>

            <form onSubmit={handleSubmit} className="admission-form">
              <div className="form-section">
                <h3>Parent Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="parentName">Parent/Guardian Name *</label>
                    <input
                      type="text"
                      id="parentName"
                      name="parentName"
                      value={formData.parentName}
                      onChange={handleChange}
                      required
                      placeholder="Enter parent/guardian name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Student Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="studentName">Student Name *</label>
                    <input
                      type="text"
                      id="studentName"
                      name="studentName"
                      value={formData.studentName}
                      onChange={handleChange}
                      required
                      placeholder="Enter student name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="dateOfBirth">Date of Birth</label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="grade">Grade Applying For *</label>
                    <select
                      id="grade"
                      name="grade"
                      value={formData.grade}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Grade</option>
                      {grades.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="institution">Preferred Institution *</label>
                    <select
                      id="institution"
                      name="institution"
                      value={formData.institution}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Institution</option>
                      {institutions.map((inst) => (
                        <option key={inst} value={inst}>
                          {inst}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="previousSchool">Previous School</label>
                  <input
                    type="text"
                    id="previousSchool"
                    name="previousSchool"
                    value={formData.previousSchool}
                    onChange={handleChange}
                    placeholder="Current/Previous school name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Additional Information</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Any additional information or questions..."
                ></textarea>
              </div>

              {status.message && (
                <div className={`status-message ${status.type}`}>
                  {status.message}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Inquiry"}
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Admissions;
